import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { CloseIcon, LiveTrackingIcon, PlusIcon } from '../chat/SidebarIcons';
import TaskViewDialog from '../tasks/TaskViewDialog';
import WorkflowViewDialog from '../chat/WorkflowViewDialog';
import PresignedAvatar from '../common/PresignedAvatar';
import { addNotification } from '../../store/slices/uiSlice';
import {
  ackLiveTrackingLocal,
  ackLiveTrackingOpened,
  enableLiveTracking,
  loadLiveTracking,
  recomputeLiveTrackingAttention,
  removeLiveTracking,
  reorderLiveTracking,
  saveLiveTrackingItem,
  setLiveTrackingLayout,
  setLiveTrackingPendingOpen,
} from '../../store/slices/liveTrackingSlice';
import { fetchTaskById, fetchTasks, upsertLiveTask } from '../../store/slices/taskSlice';
import { formatTaskAssigneeLabel, taskDelegateIds } from '../../utils/workflowTaskUtils';
import { liveTrackingSourceSignature } from '../../utils/liveTrackingLocal';
import { persistLiveTrackingItemsToRealm } from '../../realm/liveTrackingRealm';

const LANES = [
  { id: 'TEAM', label: 'Team', flexGrow: 4, accent: 'from-sky-500 to-indigo-500' },
  { id: 'PERSONAL', label: 'Personal', flexGrow: 3, accent: 'from-emerald-500 to-teal-400' },
  { id: 'BUSINESS', label: 'Business', flexGrow: 3, accent: 'from-amber-500 to-orange-500' },
];

const STICKY_NOTES = [
  { body: '#7DC8C5', head: '#A6DDDA', edge: '#3E8E8A', ink: '#1f3d3c' },
  { body: '#F2D056', head: '#F7E28A', edge: '#C4A22A', ink: '#4a3b0a' },
  { body: '#A4D06F', head: '#C4E39A', edge: '#6B9E3E', ink: '#2a4514' },
  { body: '#F3A3C0', head: '#F7C4D6', edge: '#D46B90', ink: '#5a1f35' },
  { body: '#8BB8F0', head: '#B5D4F7', edge: '#4A7FBF', ink: '#1a3358' },
  { body: '#E8B56A', head: '#F0D09A', edge: '#C48A3A', ink: '#4a3310' },
];

const htmlToText = (value) => String(value || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();

const normalizeLane = (lane) => {
  const value = String(lane || '').toUpperCase();
  if (value === 'PERSONAL' || value === 'BUSINESS') return value;
  return 'TEAM';
};

const userDisplayName = (user) => {
  if (!user) return '';
  return [user.firstName, user.lastName].filter(Boolean).join(' ').trim()
    || user.username
    || user.label
    || '';
};

const LiveTrackingPanel = ({ onClose, headerLeading = null }) => {
  const dispatch = useDispatch();
  const items = useSelector((state) => (state.liveTracking?.items || []).filter((item) => item?.outboundOp !== 'delete'));
  const attentionIds = useSelector((state) => state.liveTracking?.attentionIds || {});
  const pendingOpenId = useSelector((state) => state.liveTracking?.pendingOpenId || null);
  const loading = useSelector((state) => Boolean(state.liveTracking?.loading));
  const tasks = useSelector((state) => state.tasks?.tasks || []);
  const users = useSelector((state) => state.users?.users || []);
  const currentUserId = useSelector((state) => state.auth?.user?.id);
  const messagesByChannel = useSelector((state) => state.chat?.messages || {});
  const currentUser = useSelector((state) => state.auth?.user);
  const [addOpen, setAddOpen] = useState(false);
  const [noteTitle, setNoteTitle] = useState('');
  const [noteLane, setNoteLane] = useState('TEAM');
  const [editingId, setEditingId] = useState(null);
  const [editInfo, setEditInfo] = useState('');
  const [dragId, setDragId] = useState(null);
  const [openTask, setOpenTask] = useState(null);
  const [openWorkflow, setOpenWorkflow] = useState(null);
  const [adding, setAdding] = useState(false);
  const [liveTasks, setLiveTasks] = useState({});
  const [liveMessages, setLiveMessages] = useState({});
  const fetchedTaskIds = useRef(new Set());

  const openAddPopup = (laneId = 'TEAM') => {
    setNoteLane(normalizeLane(laneId));
    setNoteTitle('');
    setAddOpen(true);
  };

  const closeAddPopup = () => {
    if (adding) return;
    setAddOpen(false);
    setNoteTitle('');
  };

  useEffect(() => {
    dispatch(loadLiveTracking()).then(() => {
      dispatch(recomputeLiveTrackingAttention());
    });
    dispatch(fetchTasks({}));
  }, [dispatch]);

  useEffect(() => {
    const onTaskUpdated = (event) => {
      const task = event?.detail?.task;
      if (!task?.id) return;
      setLiveTasks((prev) => ({ ...prev, [String(task.id)]: task }));
      dispatch(upsertLiveTask(task));
      dispatch(recomputeLiveTrackingAttention());
    };
    window.addEventListener('workloop:task-updated', onTaskUpdated);
    return () => window.removeEventListener('workloop:task-updated', onTaskUpdated);
  }, []);

  useEffect(() => {
    const onWorkflowChanged = (event) => {
      const message = event?.detail?.message || event?.detail;
      const id = message?.id || message?.messageId;
      if (!id) return;
      setLiveMessages((prev) => ({ ...prev, [String(id)]: message }));
    };
    window.addEventListener('workloop:workflows-assigned-changed', onWorkflowChanged);
    return () => window.removeEventListener('workloop:workflows-assigned-changed', onWorkflowChanged);
  }, []);

  const ordered = useMemo(
    () => [...items].sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0)),
    [items],
  );

  useEffect(() => {
    ordered.forEach((item) => {
      if (item.itemType !== 'TASK' || !item.taskId) return;
      if (fetchedTaskIds.current.has(String(item.taskId))) return;
      fetchedTaskIds.current.add(String(item.taskId));
      dispatch(fetchTaskById(item.taskId));
    });
  }, [ordered, dispatch]);

  useEffect(() => {
    const timer = setInterval(() => {
      ordered.forEach((item) => {
        if (item.itemType === 'TASK' && item.taskId) {
          dispatch(fetchTaskById(item.taskId));
        }
      });
    }, 15000);
    return () => clearInterval(timer);
  }, [ordered, dispatch]);

  const persistLayout = (nextItems) => {
    const withOrder = nextItems.map((item, index) => ({
      ...item,
      boardLane: normalizeLane(item.boardLane),
      sortOrder: index,
    }));
    dispatch(setLiveTrackingLayout(withOrder));
    persistLiveTrackingItemsToRealm(withOrder).catch(() => {});
    dispatch(reorderLiveTracking({
      ids: withOrder.map((item) => item.id),
      lanes: Object.fromEntries(withOrder.map((item) => [item.id, normalizeLane(item.boardLane)])),
    }));
  };

  const dropOnLane = (laneId, beforeId) => {
    if (!dragId) return;
    const moving = ordered.find((item) => item.id === dragId);
    if (!moving) return;
    const rest = ordered.filter((item) => item.id !== dragId);
    const updated = { ...moving, boardLane: laneId };
    const laneItems = rest.filter((item) => normalizeLane(item.boardLane) === laneId);
    const others = rest.filter((item) => normalizeLane(item.boardLane) !== laneId);
    const insertAt = beforeId ? laneItems.findIndex((item) => item.id === beforeId) : -1;
    if (insertAt >= 0) laneItems.splice(insertAt, 0, updated);
    else laneItems.push(updated);
    persistLayout([...others, ...laneItems]);
    setDragId(null);
  };

  const addInfoBlock = async () => {
    const title = noteTitle.trim();
    if (!title) {
      dispatch(addNotification({ type: 'error', message: 'Enter a task name' }));
      return;
    }
    setAdding(true);
    try {
      await dispatch(enableLiveTracking({
        itemType: 'NOTE',
        title,
        info: '',
        boardLane: noteLane,
        assigneeUserId: currentUserId || undefined,
        assigneeName: userDisplayName(currentUser) || undefined,
      })).unwrap();
      setNoteTitle('');
      setAddOpen(false);
    } catch (error) {
      dispatch(addNotification({
        type: 'error',
        message: typeof error === 'string' ? error : 'Could not add to board',
      }));
    } finally {
      setAdding(false);
    }
  };

  const saveInfo = async (item) => {
    try {
      await dispatch(saveLiveTrackingItem({
        id: item.id,
        payload: { info: editInfo },
      })).unwrap();
      setEditingId(null);
    } catch (error) {
      dispatch(addNotification({
        type: 'error',
        message: typeof error === 'string' ? error : 'Could not save info',
      }));
    }
  };

  const findTask = (taskId) => {
    if (!taskId) return null;
    return liveTasks[String(taskId)]
      || tasks.find((row) => String(row.id) === String(taskId))
      || null;
  };

  const findMessage = (messageId) => {
    if (!messageId) return null;
    if (liveMessages[String(messageId)]) return liveMessages[String(messageId)];
    const allMessages = Object.values(messagesByChannel || {}).flat();
    return allMessages.find((row) => String(row.id) === String(messageId)) || null;
  };

  const signatureFor = (item) => liveTrackingSourceSignature(item, findTask(item.taskId), findMessage(item.sourceMessageId));

  const needsAttention = (item) => Boolean(item?.id && attentionIds[item.id]);

  const markOpened = (item) => {
    const seenSignature = signatureFor(item) || item.seenSignature || '';
    dispatch(ackLiveTrackingLocal({ id: item.id, seenSignature }));
    dispatch(ackLiveTrackingOpened({ id: item.id, seenSignature }));
  };

  const resolveAssignee = (item) => {
    if (item.itemType === 'TASK' && item.taskId) {
      const task = findTask(item.taskId);
      if (task) {
        const ids = taskDelegateIds(task);
        const firstId = ids[0] || task.createdByUserId;
        const user = users.find((row) => String(row.id) === String(firstId));
        return {
          id: firstId,
          name: formatTaskAssigneeLabel(task, users, currentUserId) || userDisplayName(user) || 'Unassigned',
          picture: user?.profilePicture || null,
        };
      }
    }
    if (item.itemType === 'WORKFLOW' && item.sourceMessageId) {
      const message = findMessage(item.sourceMessageId);
      const assigneeId = message?.workflowAssignedToUserId || item.assigneeUserId;
      const user = users.find((row) => String(row.id) === String(assigneeId));
      const name = message?.workflowAssignedToName || item.assigneeName || userDisplayName(user);
      if (name || assigneeId) {
        return { id: assigneeId, name: name || 'Assigned', picture: user?.profilePicture || null };
      }
    }
    if (item.assigneeName) {
      const user = users.find((row) => String(row.id) === String(item.assigneeUserId));
      return {
        id: item.assigneeUserId,
        name: item.assigneeName,
        picture: user?.profilePicture || null,
      };
    }
    return { id: null, name: 'Unassigned', picture: null };
  };

  const openItem = async (item) => {
    markOpened(item);
    if (item.itemType === 'TASK' && item.taskId) {
      const cached = findTask(item.taskId);
      if (cached) {
        setOpenTask(cached);
        return;
      }
      try {
        const task = await dispatch(fetchTaskById(item.taskId)).unwrap();
        setOpenTask(task);
      } catch {
        dispatch(addNotification({ type: 'error', message: 'Could not open task' }));
      }
      return;
    }
    if (item.itemType === 'WORKFLOW' && item.sourceMessageId) {
      const message = findMessage(item.sourceMessageId);
      setOpenWorkflow({
        ...(message || {}),
        messageId: item.sourceMessageId,
        channelId: item.sourceChannelId || message?.channelId,
        workflowTitle: item.title,
        id: item.sourceMessageId,
      });
    }
  };

  useEffect(() => {
    if (!pendingOpenId) return undefined;
    const item = items.find((row) => row.id === pendingOpenId);
    if (!item) return undefined;
    openItem(item);
    dispatch(setLiveTrackingPendingOpen(null));
    return undefined;
  }, [pendingOpenId, items, dispatch]);

  const renderCard = (item, index) => {
    const palette = STICKY_NOTES[index % STICKY_NOTES.length];
    const infoText = htmlToText(item.info);
    const assignee = resolveAssignee(item);
    const blinking = needsAttention(item);
    const openLabel = item.itemType === 'WORKFLOW' ? 'Open workflow' : 'Open task';
    return (
      <article
        key={item.id}
        draggable
        onDragStart={() => setDragId(item.id)}
        onDragOver={(event) => event.preventDefault()}
        onDrop={(event) => {
          event.preventDefault();
          event.stopPropagation();
          dropOnLane(normalizeLane(item.boardLane), item.id);
        }}
        onDragEnd={() => setDragId(null)}
        className={`relative min-w-0 overflow-hidden rounded-[10px] ${
          dragId === item.id ? 'opacity-60' : ''
        } ${blinking ? 'live-tracking-blink' : ''}`}
        style={{
          background: `linear-gradient(to bottom, ${palette.head} 0 18px, ${palette.body} 18px)`,
          color: palette.ink,
          boxShadow: blinking
            ? `0 0 0 2px rgba(255,255,255,0.85), 1px 1px 0 ${palette.edge}, 2px 2px 0 ${palette.edge}, 3px 3px 0 ${palette.edge}, 6px 8px 12px rgba(0,0,0,0.18)`
            : `1px 1px 0 ${palette.edge}, 2px 2px 0 ${palette.edge}, 3px 3px 0 ${palette.edge}, 6px 8px 12px rgba(0,0,0,0.18)`,
        }}
      >
        <div className="relative z-10 px-2.5 pb-2.5 pt-6">
          <div className="min-w-0">
            <div className="flex items-start justify-between gap-1">
              <span
                className="flex h-6 w-6 flex-shrink-0 cursor-grab items-center justify-center rounded-md text-[11px] font-black"
                style={{ background: 'rgba(255,255,255,0.45)' }}
              >
                {index + 1}
              </span>
              <span
                className="rounded-full px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide"
                style={{ background: 'rgba(255,255,255,0.45)' }}
              >
                {item.itemType === 'TASK' ? 'Task' : item.itemType === 'WORKFLOW' ? 'Workflow' : 'Info'}
              </span>
            </div>
            <button
              type="button"
              className="mt-1.5 line-clamp-3 w-full text-left text-sm font-bold leading-snug"
              onClick={() => openItem(item)}
            >
              {item.title || 'Untitled'}
            </button>
            {item.itemType !== 'NOTE' || assignee.id ? (
              <div
                className="mt-2 inline-flex max-w-full items-center gap-1 rounded-full px-1.5 py-0.5"
                style={{ background: 'rgba(255,255,255,0.45)' }}
              >
                <PresignedAvatar
                  fileKey={assignee.picture}
                  cacheKey={assignee.id}
                  name={assignee.name}
                  className="h-4 w-4 rounded-full object-cover"
                />
                <span className="truncate text-[10px] font-semibold">{assignee.name}</span>
              </div>
            ) : null}
            <div className="mt-1.5 flex flex-wrap items-center gap-1">
              {item.itemType !== 'NOTE' ? (
                <button
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    openItem(item);
                  }}
                  className="rounded-full px-2 py-1 text-[10px] font-bold hover:opacity-90"
                  style={{ background: 'rgba(255,255,255,0.7)' }}
                >
                  {openLabel}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => dispatch(removeLiveTracking(item.id))}
                  className="rounded-full px-2 py-1 text-[10px] font-semibold hover:opacity-80"
                  style={{ background: 'rgba(0,0,0,0.12)' }}
                >
                  Remove
                </button>
              )}
            </div>
            {editingId === item.id ? (
              <div className="mt-2">
                <textarea
                  value={editInfo}
                  onChange={(event) => setEditInfo(event.target.value)}
                  rows={3}
                  className="w-full resize-none rounded-xl border px-3 py-2 text-sm focus:outline-none"
                  style={{
                    borderColor: 'rgba(0,0,0,0.12)',
                    background: 'rgba(255,255,255,0.55)',
                    color: palette.ink,
                  }}
                />
                <div className="mt-2 flex gap-2">
                  <button
                    type="button"
                    onClick={() => saveInfo(item)}
                    className="rounded-lg bg-white px-2.5 py-1 text-xs font-semibold shadow-sm"
                    style={{ color: palette.ink }}
                  >
                    Save
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditingId(null)}
                    className="rounded-lg px-2.5 py-1 text-xs font-semibold"
                    style={{ background: 'rgba(0,0,0,0.12)' }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => {
                  setEditingId(item.id);
                  setEditInfo(item.info || '');
                }}
                className="mt-1.5 line-clamp-3 block w-full text-left text-xs opacity-80"
              >
                {infoText || 'Add info for this block…'}
              </button>
            )}
          </div>
        </div>
      </article>
    );
  };

  return (
    <div className="flex flex-col h-full min-w-0 overflow-x-hidden bg-white dark:bg-gray-800">
      <div className="flex items-center justify-between gap-2 px-3 sm:px-4 py-2.5 sm:py-3 border-b border-gray-200 dark:border-gray-700 flex-shrink-0 flex-wrap">
        <div className="flex items-center space-x-2 min-w-0">
          {headerLeading}
          <LiveTrackingIcon className="w-5 h-5 text-primary-500 flex-shrink-0" />
          <div className="min-w-0">
            <h2 className="text-sm font-semibold text-gray-900 dark:text-white truncate">
              Live Tracking Tasks
            </h2>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {ordered.length} active {ordered.length === 1 ? 'item' : 'items'} · drag between Team, Personal, Business
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => openAddPopup('TEAM')}
            className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300 transition-colors"
            title="Add to board"
          >
            <PlusIcon className="w-5 h-5" />
          </button>
          {typeof onClose === 'function' && (
            <button
              type="button"
              onClick={onClose}
              className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400 transition-colors"
              title="Close panel"
            >
              <CloseIcon className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-hidden bg-white dark:bg-gray-800">
        {loading && !ordered.length ? (
          <p className="px-4 py-4 text-sm text-gray-500 dark:text-gray-400">Loading live tracking…</p>
        ) : (
          <div className="flex h-full min-h-0 flex-col lg:flex-row">
            {LANES.map((lane) => {
              const laneItems = ordered.filter((item) => normalizeLane(item.boardLane) === lane.id);
              return (
                <section
                  key={lane.id}
                  onDragOver={(event) => event.preventDefault()}
                  onDrop={(event) => {
                    event.preventDefault();
                    dropOnLane(lane.id);
                  }}
                  className="flex min-h-0 min-w-0 flex-1 flex-col border-b lg:border-b-0 lg:border-r border-gray-200 dark:border-gray-700 last:border-r-0"
                  style={{ flexGrow: lane.flexGrow, flexBasis: 0 }}
                >
                  <div className={`flex-shrink-0 px-3 py-2.5 bg-gradient-to-r ${lane.accent}`}>
                    <div className="flex items-center justify-between text-white gap-2">
                      <h3 className="text-sm font-bold">{lane.label}</h3>
                      <div className="flex items-center gap-1.5">
                        <span className="rounded-full bg-white/20 px-2 py-0.5 text-[11px] font-semibold tabular-nums">
                          {laneItems.length}
                        </span>
                        <button
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation();
                            openAddPopup(lane.id);
                          }}
                          className="rounded-lg bg-white/20 p-1 hover:bg-white/30"
                          title={`Add to ${lane.label}`}
                        >
                          <PlusIcon className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="min-h-0 flex-1 overflow-y-auto p-3 pr-4 pb-5">
                    {laneItems.length === 0 ? (
                      <div className="rounded-xl border border-dashed border-gray-200 dark:border-gray-600 px-3 py-8 text-center text-xs text-gray-500 dark:text-gray-400">
                        Drop work here
                      </div>
                    ) : (
                      <div className="grid grid-cols-2 gap-x-3 gap-y-4">
                        {laneItems.map((item, index) => renderCard(item, index))}
                      </div>
                    )}
                  </div>
                </section>
              );
            })}
          </div>
        )}
      </div>

      {openTask ? (
        <TaskViewDialog
          task={openTask}
          onClose={() => setOpenTask(null)}
          zIndexClass="z-[80]"
        />
      ) : null}
      {openWorkflow ? (
        <WorkflowViewDialog
          item={openWorkflow}
          onClose={() => setOpenWorkflow(null)}
          zIndexClass="z-[80]"
        />
      ) : null}

      {addOpen
        ? ReactDOM.createPortal(
          <div className="fixed inset-0 z-[2100] overflow-y-auto">
            <div className="fixed inset-0 bg-black bg-opacity-50" onClick={closeAddPopup} />
            <div className="flex min-h-full items-center justify-center p-4">
              <div
                className="relative w-full max-w-md rounded-lg bg-white dark:bg-gray-800 shadow-xl"
                onClick={(event) => event.stopPropagation()}
              >
                <div className="flex items-center justify-between border-b border-gray-200 dark:border-gray-700 px-5 py-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Add to board</h3>
                  <button
                    type="button"
                    onClick={closeAddPopup}
                    className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
                    title="Close"
                  >
                    <CloseIcon className="w-5 h-5" />
                  </button>
                </div>
                <form
                  className="px-5 py-4 space-y-4"
                  onSubmit={(event) => {
                    event.preventDefault();
                    addInfoBlock();
                  }}
                >
                  <label className="block">
                    <span className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Task name
                    </span>
                    <input
                      autoFocus
                      value={noteTitle}
                      onChange={(event) => setNoteTitle(event.target.value)}
                      placeholder="Enter task name"
                      className="w-full rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-white placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500/40"
                    />
                  </label>
                  <label className="block">
                    <span className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Category
                    </span>
                    <select
                      value={noteLane}
                      onChange={(event) => setNoteLane(event.target.value)}
                      className="w-full rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/40"
                    >
                      {LANES.map((lane) => (
                        <option key={lane.id} value={lane.id}>{lane.label}</option>
                      ))}
                    </select>
                  </label>
                  <div className="flex items-center justify-end gap-2 pt-1">
                    <button
                      type="button"
                      onClick={closeAddPopup}
                      className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={adding || !noteTitle.trim()}
                      className="px-4 py-2 text-sm font-medium rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-60"
                    >
                      {adding ? 'Adding…' : 'Add to board'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>,
          document.body,
        )
        : null}
    </div>
  );
};

export default LiveTrackingPanel;
